'use client'

import { captureException } from '@sentry/nextjs'
import { useQueryClient } from '@tanstack/react-query'
import { useRouter } from 'next/navigation'
import { useEffect, useRef } from 'react'
import {
  rotateAuthCacheScope,
  setAuthQueryIdentity,
  syncAuthCacheScopeOwner,
} from '@/hooks/use-auth-cache-scope'
import { currentUserQueryOptions, type UserWithProfile } from '@/hooks/use-user'
import { createClient } from '@/lib/supabase/client'

/**
 * Listens to Supabase auth state changes across tabs.
 * When the user changes (e.g. sign-in on another tab) or signs out,
 * clears cached data and redirects to login.
 */
export const AuthSync = () => {
  const router = useRouter()
  const queryClient = useQueryClient()
  const currentUserIdRef = useRef<string | null>(null)

  useEffect(() => {
    const supabase = createClient()
    const userOptions = currentUserQueryOptions()
    let active = true
    let receivedAuthEvent = false
    let resolvedAuthSession = false
    let authEventVersion = 0
    let authCheckTimer: ReturnType<typeof setTimeout> | undefined
    let latestAuthEvent = ''

    const resetForUser = (userId: string | null) => {
      rotateAuthCacheScope()
      queryClient.clear()
      currentUserIdRef.current = userId
      if (userId) {
        window.location.reload()
      } else if (window.location.pathname !== '/login') {
        router.push('/login')
      }
    }

    // Capture initial user ID through the shared React Query cache so this
    // listener does not add another Supabase auth/user request on cold routes.
    const cachedUser = queryClient.getQueryData<UserWithProfile | null>(userOptions.queryKey)
    if (cachedUser === undefined) {
      queryClient
        .ensureQueryData(userOptions)
        .then((user) => {
          if (active && !receivedAuthEvent) {
            currentUserIdRef.current = user?.id ?? null
          }
        })
        .catch(() => {
          if (active && !receivedAuthEvent) {
            currentUserIdRef.current = null
          }
        })
    } else {
      currentUserIdRef.current = cachedUser?.id ?? null
    }

    // The workspace stream can hydrate after INITIAL_SESSION. Check that late
    // snapshot too, once hydration finishes, so clearing cannot interleave with
    // the remaining queries being inserted by HydrationBoundary during render.
    const unsubscribeCache = queryClient.getQueryCache().subscribe((event) => {
      if (
        !resolvedAuthSession ||
        (event.type !== 'added' && event.type !== 'updated') ||
        event.query.queryKey[0] !== userOptions.queryKey[0]
      ) {
        return
      }
      queueMicrotask(() => {
        if (!active) {
          return
        }
        const user = queryClient.getQueryData<UserWithProfile | null>(userOptions.queryKey)
        if (user && user.id !== currentUserIdRef.current) {
          resetForUser(currentUserIdRef.current)
        }
      })
    })

    const reconcileSession = (event: string, newUserId: string | null) => {
      resolvedAuthSession = true
      setAuthQueryIdentity(queryClient, newUserId)
      const hydratedUserId = queryClient.getQueryData<UserWithProfile | null>(
        userOptions.queryKey
      )?.id

      if (event === 'SIGNED_OUT' && newUserId === null) {
        resetForUser(null)
        return
      }

      if (
        (currentUserIdRef.current !== null && newUserId !== currentUserIdRef.current) ||
        (hydratedUserId !== undefined && newUserId !== hydratedUserId)
      ) {
        // INITIAL_SESSION can already differ from the server snapshot if
        // another tab changed the account while the document was loading.
        // Hydration may also have landed since this listener was registered.
        resetForUser(newUserId)
        return
      }

      if (newUserId) {
        syncAuthCacheScopeOwner(newUserId)
      }

      // Update ref for TOKEN_REFRESHED or initial SIGNED_IN
      currentUserIdRef.current = newUserId
    }

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      receivedAuthEvent = true
      latestAuthEvent = event
      authEventVersion += 1
      if (authCheckTimer !== undefined) {
        return
      }

      // Cross-tab broadcasts can describe an old session while our cookies
      // already contain another account. Reloading from the event payload
      // loops on every broadcast. Read the stored session outside the auth
      // callback, which runs under Supabase's session lock.
      authCheckTimer = setTimeout(async () => {
        authCheckTimer = undefined
        const version = authEventVersion
        const checkedEvent = latestAuthEvent
        let result: Awaited<ReturnType<typeof supabase.auth.getSession>>
        try {
          result = await supabase.auth.getSession()
        } catch (error) {
          captureException(error, { tags: { area: 'auth-sync', endpoint: 'session-check' } })
          return
        }
        if (!active || version !== authEventVersion) {
          return
        }
        if (result.error) {
          captureException(result.error, { tags: { area: 'auth-sync', endpoint: 'session-check' } })
          return
        }
        reconcileSession(checkedEvent, result.data.session?.user?.id ?? null)
      }, 0)
    })

    return () => {
      active = false
      clearTimeout(authCheckTimer)
      unsubscribeCache()
      subscription.unsubscribe()
    }
  }, [router, queryClient])

  return null
}
